import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Award, BookOpen, Briefcase, Building2, Clock, KeyRound, Mail, Save, UserRound } from 'lucide-react';
import { perfilService } from '../../shared/services';
import type { Perfil } from '../../shared/types';

type Aba = 'visao' | 'dados' | 'seguranca';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

function formatarTempo(minutos: number) {
  if (!minutos || minutos <= 0) return '0h';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas}h` : `${horas}h ${resto}min`;
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<Aba>('visao');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  const { data: perfil, isLoading, isError } = useQuery({
    queryKey: ['perfil'],
    queryFn: perfilService.getMeuPerfil,
  });

  useEffect(() => {
    if (!perfil) return;
    setNome(perfil.nome ?? '');
    setTelefone(perfil.telefone ?? '');
    setAvatarUrl(perfil.avatarUrl ?? '');
  }, [perfil]);

  const salvarDados = useMutation({
    mutationFn: () => perfilService.atualizar({ nome, telefone, avatarUrl }),
    onSuccess: (atualizado: Perfil) => {
      queryClient.setQueryData(['perfil'], atualizado);
      setAviso({ tipo: 'ok', texto: 'Dados atualizados.' });
    },
    onError: (error) => setAviso({ tipo: 'erro', texto: mensagemDeErro(error, 'Não foi possível salvar os dados.') }),
  });

  const alterarSenha = useMutation({
    mutationFn: () => perfilService.alterarSenha(senhaAtual, novaSenha),
    onSuccess: () => {
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmacao('');
      setAviso({ tipo: 'ok', texto: 'Senha alterada com sucesso.' });
    },
    onError: (error) => setAviso({ tipo: 'erro', texto: mensagemDeErro(error, 'Não foi possível alterar a senha.') }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !perfil) {
    return <div className="p-8 text-center text-red-600">Não foi possível carregar seu perfil.</div>;
  }

  const iniciais = perfil.nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase())
    .join('');

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="card flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {perfil.avatarUrl ? (
            <img src={perfil.avatarUrl} alt={perfil.nome} className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-bold">
              {iniciais || <UserRound />}
            </div>
          )}

          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold text-gray-900">{perfil.nome}</h1>
            <p className="text-gray-600 flex items-center justify-center sm:justify-start gap-2 mt-1">
              <Mail size={16} /> {perfil.email}
            </p>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start mt-3 text-sm">
              {perfil.cargoNome && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-gray-700">
                  <Briefcase size={14} /> {perfil.cargoNome}
                </span>
              )}
              {perfil.areaNome && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-gray-700">
                  <Building2 size={14} /> {perfil.areaNome}
                </span>
              )}
              {perfil.empresaNome && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-gray-700">
                  <Building2 size={14} /> {perfil.empresaNome}
                </span>
              )}
            </div>
          </div>

          <Link to="/certificados" className="btn-secondary flex items-center gap-2">
            <Award size={18} /> Certificados
          </Link>
        </div>

        <div className="flex gap-2 mt-6 border-b border-gray-200">
          {([
            ['visao', 'Visão geral'],
            ['dados', 'Dados pessoais'],
            ['seguranca', 'Segurança'],
          ] as Array<[Aba, string]>).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => {
                setAba(valor);
                setAviso(null);
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                aba === valor
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>

        {aviso && (
          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm ${
              aviso.tipo === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
            }`}
          >
            {aviso.texto}
          </div>
        )}

        {aba === 'visao' && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { rotulo: 'Cursos concluídos', valor: perfil.cursosConcluidos, Icon: BookOpen },
                { rotulo: 'Trilhas concluídas', valor: perfil.trilhasConcluidas, Icon: Award },
                { rotulo: 'Certificados', valor: perfil.certificadosEmitidos, Icon: Award },
                { rotulo: 'Tempo de estudo', valor: formatarTempo(perfil.minutosEstudo), Icon: Clock },
              ].map(({ rotulo, valor, Icon }) => (
                <div key={rotulo} className="card">
                  <Icon className="text-primary-600 mb-2" size={20} />
                  <p className="text-2xl font-bold text-gray-900">{valor}</p>
                  <p className="text-sm text-gray-600">{rotulo}</p>
                </div>
              ))}
            </div>

            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Vínculo</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {[
                  ['Empresa', perfil.empresaNome],
                  ['Área', perfil.areaNome],
                  ['Cargo', perfil.cargoNome],
                  ['Gestor', perfil.gestorNome],
                  ['Matrícula', perfil.matricula],
                  ['Admissão', formatarData(perfil.admissao)],
                  ['Último acesso', formatarData(perfil.ultimoAcesso)],
                  ['Perfil de acesso', perfil.papeis.join(', ')],
                ].map(([rotulo, valor]) => (
                  <div key={rotulo}>
                    <dt className="text-gray-500">{rotulo}</dt>
                    <dd className="text-gray-900 font-medium">{valor || '—'}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {aba === 'dados' && (
          <form
            className="card mt-6 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setAviso(null);
              salvarDados.mutate();
            }}
          >
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                id="nome"
                value={nome}
                onChange={(event) => setNome(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                minLength={2}
              />
            </div>

            <div>
              <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                id="telefone"
                value={telefone}
                onChange={(event) => setTelefone(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <label htmlFor="avatar" className="block text-sm font-medium text-gray-700 mb-1">URL do avatar</label>
              <input
                id="avatar"
                value={avatarUrl}
                onChange={(event) => setAvatarUrl(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="https://..."
              />
            </div>

            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
              CPF, empresa, área, cargo e gestor são mantidos pelo RH e não podem ser editados aqui.
            </div>

            <button type="submit" disabled={salvarDados.isPending} className="btn-primary flex items-center gap-2 disabled:opacity-60">
              <Save size={16} /> {salvarDados.isPending ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </form>
        )}

        {aba === 'seguranca' && (
          <form
            className="card mt-6 space-y-4 max-w-lg"
            onSubmit={(event) => {
              event.preventDefault();
              setAviso(null);
              if (novaSenha !== confirmacao) {
                setAviso({ tipo: 'erro', texto: 'A confirmação não confere com a nova senha.' });
                return;
              }
              alterarSenha.mutate();
            }}
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <KeyRound size={18} /> Alterar senha
            </h2>

            <div>
              <label htmlFor="senha-atual" className="block text-sm font-medium text-gray-700 mb-1">Senha atual</label>
              <input
                id="senha-atual"
                type="password"
                value={senhaAtual}
                onChange={(event) => setSenhaAtual(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                autoComplete="current-password"
              />
            </div>

            <div>
              <label htmlFor="nova-senha" className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
              <input
                id="nova-senha"
                type="password"
                value={novaSenha}
                onChange={(event) => setNovaSenha(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo de 8 caracteres.</p>
            </div>

            <div>
              <label htmlFor="confirmar" className="block text-sm font-medium text-gray-700 mb-1">Confirmar nova senha</label>
              <input
                id="confirmar"
                type="password"
                value={confirmacao}
                onChange={(event) => setConfirmacao(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" disabled={alterarSenha.isPending} className="btn-primary disabled:opacity-60">
              {alterarSenha.isPending ? 'Alterando...' : 'Alterar senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
